import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { getItem } from "../api/jobExplorer";
import { ChevronDown, ChevronUp, Loader2, ArrowLeft } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import { ProsContext } from "../context/ProsContext";
import { HistoryContext } from "../context/HistoryContext";
import PremiumModal from "../components/premium/PremiumModal";
import { useFirstname } from "../hooks/useFirstname";
import { useNavigate } from 'react-router-dom';
import { updateLuxMetiersVariables } from "../utils/luxVariables";
import { isProAccessBlocked } from "../config/freemium";

const Section = ({ title, children }) => (
  <section className="space-y-4">
    {title && (
      <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
        {title}
      </h2>
    )}
    {children}
  </section>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-xl bg-slate-800/70 p-4 backdrop-blur-md ${className}`}
  >
    {children}
  </div>
);

// -----------------------------------------------------------------------------
//  En‑tête du métier avec image de fond "cover" + blur
// -----------------------------------------------------------------------------
const JobHeader = ({ job, gender }) => {
  const navigate = useNavigate();
  return (
  <div className="relative h-64 w-full overflow-hidden rounded-xl">
    <img
      src={gender === 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1]}
      alt={gender === 'boy' ? job.title_m : job.title_f}
      className="absolute inset-0 h-full w-full object-cover lg:blur-sm"
    />
    {/* Dégradé pour liseré bleu */}
    <div className="absolute inset-0 lg:bg-gradient-to-b  lg:from-blue-600/60  lg:to-indigo-900/70" />

    <div className="relative left-14 top-2 md:left-2 flex items-center gap-3 cursor-pointer" onClick={() => navigate(-1)}>
        <div className=" rounded-full p-2 bg-slate-800"><ArrowLeft className="w-4 h-4" /></div>
    </div>
    <div className="relative z-10 flex h-full w-full flex-col items-center justify-center text-center text-white">
      <div className="text-3xl font-bold">{gender === 'boy' ? job.title_m : job.title_f}</div>
      {/* <p className="mt-1 text-sm opacity-90 md:max-w-[50%]">{job.short_description_m}</p> */}
      {job._categories.map( (category) => (
        <span key={`category_${category.ciaov2_category_id}`} className="mt-3 rounded-full bg-emerald-500/45 px-3 py-1 text-xs font-medium text-emerald-300 border border-emerald-300 border-opacity-20 shadow-inner">
          {category._category.label}
        </span>
      ))}
    </div>
  </div>
)};

// -----------------------------------------------------------------------------
//  Mini‑cartes info (salaire, diplôme, etc.)
// -----------------------------------------------------------------------------
const StatCard = ({ icon, label, children }) => (
  <Card className="flex items-center gap-4 border border-opacity-40 border-teal-500">
    <span className="text-2xl">{icon}</span>
    <div className="flex flex-col text-white/90">
      <span className="font-semibold text-white">{children}</span>
      <span className="text-xs opacity-70">{label}</span>
    </div>
  </Card>
);

// -----------------------------------------------------------------------------
//  Tags (Qualités essentielles)
// -----------------------------------------------------------------------------
const Tag = ({ children }) => (
  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-400 bg-opacity-20 text-blue-400">
    #{children}
  </span>
);

const EssentialQualities = ({ job, toggleDetailQuality, isOpen, onClickQuizz }) => (
  <Section title="✨ Qualités essentielles">
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex flex-wrap gap-2 mb-3">
        {job._qualities.map((quality) => (
          <Tag key={`quality_${quality.ciaov2_quality_id}`}>{quality._quality.label}</Tag>
        ))}
      </div>
      { !isOpen && 
        <button className="text-sm text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-2"
          onClick={toggleDetailQuality}>
          Voir le détail <ChevronDown size={15} />
        </button>
      }
      { isOpen && 
        <button className="text-sm text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-2"
          onClick={toggleDetailQuality}>
          Replier <ChevronUp size={15} />
        </button>
      }
      { isOpen && 
        <div className="flex flex-wrap gap-3 mb-3 mt-4">
          {job._qualities.map((quality) => (
            <div key={`quality_detail_${quality.ciaov2_quality_id}`} className="text-sm">
              <span className="font-semibold  text-blue-400">• {quality._quality.label}</span> : {quality.explanation}
            </div>
          ))}
        </div>
      }
      <div className="mt-4 pt-4 border-t border-gray-700">
        <button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-4 rounded-lg font-semibold flex flex-col items-center justify-center gap-1 hover:brightness-110"
          onClick={onClickQuizz}>
          <div className="flex items-center gap-2">🎯 Ce métier est-il fait pour toi ?</div>
          <span className="text-sm opacity-90">4 min • 6 questions</span>
        </button>
      </div>
    </div>
  </Section>
);

// -----------------------------------------------------------------------------
//  Matières clés au lycée
// -----------------------------------------------------------------------------
const HighSchoolSubjects = ({ job }) => (
  <Section title="Matières clés au lycée">
    <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
      {job.highSchoolSubjects.map((s) => (
        <Card key={s.name} className="flex flex-col items-center gap-1 py-6">
          <span className="text-4xl">📚</span>
          <span className="text-sm font-semibold text-white">{s.name}</span>
          <span className="text-xs text-amber-400">{s.importance}</span>
        </Card>
      ))}
    </div>
  </Section>
);

// -----------------------------------------------------------------------------
//  Planning d'une journée type
// -----------------------------------------------------------------------------
const TypicalDay = ({ job }) => (
  <Section title="Une journée type">
    <Card className="space-y-3 bg-slate-800/50">
      {job.daySchedule.map(({ time, icon, activity }) => (
        <div key={time} className="flex items-start gap-3 text-sm">
          <span className="w-12 shrink-0 text-right font-bold text-emerald-300">
            {time}
          </span>
          <span className="text-xl">{icon}</span>
          <p className="flex-1 text-white/90">{activity}</p>
        </div>
      ))}
    </Card>
  </Section>
);

// -----------------------------------------------------------------------------
//  Cadre de travail (citation + bouton "Pose tes questions")
// -----------------------------------------------------------------------------
const WorkEnvironment = ({ job, gender, onClickChat, loading }) => (
  <Section title="🏢 Au quotidien">
    <Card className="space-y-4 bg-slate-800/70">
      <blockquote className="italic leading-snug text-white/90">
        “{job.quote}”
        {/* <footer className="mt-2 text-xs opacity-70">– {job.quoteAuthor}</footer> */}
      </blockquote>
      <button className="flex flex-col items-center w-full rounded-xl bg-emerald-400 py-3 text-center text-sm font-semibold text-slate-900 shadow hover:brightness-110"
        onClick={onClickChat}
        disabled={loading}>
        <div className="flex items-center gap-2">
          {loading ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black"></div> : <span>💬</span>}
          Pose tes questions à un pro
        </div>
        <span className="text-xs font-normal opacity-80">
          Aucune mauvaise question, que de la découverte !
        </span>
      </button>
    </Card>
  </Section>
);

// -----------------------------------------------------------------------------
//  Cadre de travail (citation + bouton "Pose tes questions")
// -----------------------------------------------------------------------------
const Mission = ({ job, gender }) => (
  <Section title="🧭 Rôle principal">
    <Card className="space-y-4 bg-slate-800/70">
      <blockquote className="leading-snug text-white/90">
        {gender === 'boy' ? job.short_description_m : job.short_description_f}
        {/* <footer className="mt-2 text-xs opacity-70">– {job.quoteAuthor}</footer> */}
      </blockquote>
    </Card>
  </Section>
);

// -----------------------------------------------------------------------------
//  Épanouissement (points + défis) & "Pas fait pour toi si..."
// -----------------------------------------------------------------------------
const Fulfillment = ({ job, made_for_you }) => (
  <Section title="😊 Épanouissement">
    {/* <div className="grid gap-4 md:grid-cols-2"> */}
      {/* Points positifs */}
      <div className="bg-green-400 bg-opacity-10 rounded-xl p-4 border border-green-400 border-opacity-20">
        <h4 className="font-bold text-white mb-3 flex items-center gap-2">
          <span className="text-2xl">😍</span>
          <span className="text-green-400">Points positifs</span>
        </h4>
        <div className="gap-2 grid md:grid-cols-2">
          {made_for_you.map( (item) => (
            <div key={item.text} className="text-sm text-gray-200 flex items-start">
              {item.emoji} {item.text}
            </div>
          ))}
          
        </div>
      </div>

      {/* Les défis */}
      {/* <Card className="bg-amber-600/10 ring-1 ring-amber-600/30">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-300">
          😬 Les défis
        </h3>
        <ul className="space-y-1 text-sm text-white/90">
          {job.fulfillment.challenges.map((c) => (
            <li key={c} className="flex items-start gap-2">
              <span className="text-amber-400">⚠️</span>
              {c}
            </li>
          ))}
        </ul>
      </Card>
    </div> */}

    {/* Pas fait pour toi si… */}
    {/* <Card className="mt-4 bg-rose-600/10 ring-1 ring-rose-600/30">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-400">
        ⚠️ Pas fait pour toi si…
      </h3>
      <ul className="space-y-1 text-sm text-white/90">
        {job.notForYou.map((n) => (
          <li key={n} className="flex items-start gap-2">
            <span className="text-rose-400">△</span>
            {n}
          </li>
        ))}
      </ul>
    </Card> */}
  </Section>
);

// -----------------------------------------------------------------------------
//  Comment y arriver (timeline)
// -----------------------------------------------------------------------------
const TimelineItem = ({ idx, item, isLast }) => (
  <div className="relative pl-16">
    {/* Ligne verte verticale */}
    <span className="absolute left-7 top-0 h-full w-px bg-emerald-400/50" />
    {isLast && (
      <span className="absolute left-7 bottom-0 h-4 w-px bg-transparent" />
    )}

    {/* Puce année */}
    <span className="absolute left-0 top-1 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-slate-900">
      ans
    </span>

    {/* Contenu */}
    <Card className="ml-4 bg-slate-700/50">
      <div className="text-sm font-semibold text-white">{item.title}</div>
      <div className="text-xs text-white/70">{item.duration}</div>
      <p className="mt-1 text-xs text-white/90">{item.details}</p>
    </Card>
  </div>
);

const HowToArrive = ({ job }) => (
  <Section title="Comment y arriver">
    <div className="space-y-4">
      {job.pathway.map((p, i) => (
        <TimelineItem
          key={p.title}
          idx={i}
          item={p}
          isLast={i === job.pathway.length - 1}
        />
      ))}
    </div>
    <Card className="mt-4 text-xs text-amber-200">
      💡 Astuce : Tu peux faire des stages dès la licence pour découvrir différents types de droit !
    </Card>
  </Section>
);

// -----------------------------------------------------------------------------
//  Évolution de carrière
// -----------------------------------------------------------------------------
const CareerEvolution = ({ job, onClickGritAndFit }) => (
  <Section title="📈 Évolution de carrière">
    <div className="space-y-3">
      {job.salary_evolutions.map((step) => (
        <Card key={step.experience} className="flex items-start justify-between gap-4 bg-slate-700/60">
          <div>
            <div className="text-sm font-semibold text-white">{step.experience}</div>
            <div className="text-xs text-emerald-300">{step.experience_salary}/mois</div>
            <p className="mt-1 text-xs text-white/90">{step.experience_role}</p>
          </div>
          <span className="shrink-0 rounded-md bg-slate-600 px-2 py-1 text-[10px] font-medium text-white/70">
            {step.experience_years}
          </span>
        </Card>
      ))}
    </div>
    <button className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 px-6 rounded-xl font-bold flex flex-col items-center justify-center gap-1 hover:brightness-110"
      onClick={onClickGritAndFit}>
      <div className="flex items-center gap-3">🎮 Vis le métier en vrai</div>
      <span className="text-sm font-normal opacity-90">7-10 min • 3 défis</span>
    </button>
  </Section>
);

export default function JobDetail({ onSend, initActivity }) {
  const { jobId, gender }                         = useParams();
  const [job, setJob]                             = useState(null);
  const [isLoading, setIsLoading]                 = useState(true);
  const [isQualityOpen, setIsQualityOpen]         = useState(false);
  const { user, updateUser }                                  = useContext(AuthContext);
  const { itemsPro, setItemsPro }                 = useContext(ProsContext);
  const { addAction }                             = useContext(HistoryContext);
  const { getRandomFirstname }                    = useFirstname();
  const navigate                                  = useNavigate();
  const [showPremiumModal, setShowPremiumModal]   = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');

  useEffect(() => {
    (async () => {
      const job = await getItem(jobId)
      setJob(job)
      setIsLoading(false)
      const newHistory = await addAction({type: "JOB_PAGE", values: {title: job.title_m, id: job.id}})
      await updateLuxMetiersVariables(user.id, newHistory, itemsPro)
    })();
  }, [jobId]);

  const toggleDetailQuality = () => {
    setIsQualityOpen(!isQualityOpen)
  }

  async function handleClickQuizz() {
    if (isProAccessBlocked(user, itemsPro, jobId)) {
      const jobTitle = gender === 'boy' ? job.title_m : job.title_f
      const startsWithVowel = /^[aeiouàâäéèêëïîôùûü]/i.test(jobTitle)
      setPremiumModalReason(`se tester dans le métier ${startsWithVowel ? "d'" : "de "}${jobTitle}`)
      setShowPremiumModal(true)
      return
    }

    const path = await getPath('quizz')
    navigate(path);
  }

  async function handleClickGritAndFit() {
    if (isProAccessBlocked(user, itemsPro, jobId)) {
      const jobTitle = gender === 'boy' ? job.title_m : job.title_f
      const startsWithVowel = /^[aeiouàâäéèêëïîôùûü]/i.test(jobTitle)
      setPremiumModalReason(`faire une immersion dans le métier ${startsWithVowel ? "d'" : "de "}${jobTitle}`)
      setShowPremiumModal(true)
      return
    }
    const path = await getPath('grit-and-fit')
    navigate(path);
  }

  async function getPath(type) {
    let name
    if ( !itemsPro[job.id] ) {
      name = gender === 'boy' ? getRandomFirstname('male') : getRandomFirstname('female')
      setItemsPro({
        ciaov2_job_id: job.id,
        name,
        gender,
        title: gender === 'boy' ? job.title_m : job.title_f,
      })
    }
    const made_for_you = user.age < 16 ? job.made_for_you_14_v2 : job.made_for_you_16_v2
    const values = {
      jobId: job.id,
      gender,
      title: gender === 'boy' ? job.title_m : job.title_f,
      photo_url: gender === 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1],
      type: type,
      made_for_you: made_for_you.map((item) => item.text).join("\n"),
      from: "job_detail"
    }
    let path = await initActivity(values)
    return path
  }

  async function handleClickChat() {
    if (isProAccessBlocked(user, itemsPro, jobId)) {
      const jobTitle = gender === 'boy' ? job.title_m : job.title_f
      setPremiumModalReason(`échanger avec ${jobTitle}`)
      setShowPremiumModal(true)
      return
    }
    let name
    if ( itemsPro[job.id] ) {
      name = itemsPro[job.id].name
    } else {
      name = gender === 'boy' ? getRandomFirstname('male') : getRandomFirstname('female')
      setItemsPro({
        ciaov2_job_id: job.id,
        name,
        gender,
        title: gender === 'boy' ? job.title_m : job.title_f,
      })
    }

    const qualities = job._qualities.map( (quality) => `${quality._quality.label} : ${quality.explanation}` ).join("\n")
    const level_of_education = job._study_levels.map( (study_level) => study_level._study_level.label ).join(", ")
    const salary = `Salaire début de carrière de ${job.salary_min}€ pouvant aller jusqu'à ${job.salary_max}€. ${job.salary_explanation}`
    const salary_evolution_explanation = job.salary_evolutions.map( (salary_evolution) => 
      `${salary_evolution.experience} (${salary_evolution.experience_years}) gagne environ ${salary_evolution.experience_salary}. Role : ${salary_evolution.experience_role}.`
    ).join("\n")
    const values = {
      id: job.id,
      title: gender == 'boy' ? job.title_m : job.title_f,
      title_for_prompt: gender == 'boy' ? job.title_for_prompt_m : job.title_for_prompt_f,
      name,
      picture: gender == 'boy' ? job.aws_pictures_m[1] : job.aws_pictures_f[1],
      qualities: qualities,
      salary: salary,
      salary_evolution_explanation: salary_evolution_explanation,
      level_of_education: level_of_education,
      gender,
      from: "job_detail"
    }
    let path = await onSend(values)
    navigate(path);
  }

  return (
    <div className="relative w-full">
      <main className="h-dvh overflow-y-auto w-full mx-auto max-w-6xl overflow-x-hidden no-scrollbar space-y-8  p-4 md:p-8 w-full items-center">
        { isLoading && <Loader2 className="w-full animate-spin mt-10 text-white" size={40}/>}
        { job && <JobHeader job={job} gender={gender} /> }

        { job && 
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard label={job.salary_explanation_short} icon="💶">
              {job.salary_min.toLocaleString("fr-FR")}€ – {job.salary_max.toLocaleString("fr-FR")}€ / mois
            </StatCard>
            <StatCard label="" icon="">
              {job._study_levels.map( (study_level) => `${study_level._study_level.icon} ${study_level._study_level.label}`).join(' • ')}
            </StatCard>
          </div>
        }

        { job && <Mission job={job} gender={gender} /> }
        { job && <EssentialQualities job={job} toggleDetailQuality={toggleDetailQuality} isOpen={isQualityOpen} onClickQuizz={handleClickQuizz} /> }
        {/* <HighSchoolSubjects job={job_fake} />
        <TypicalDay job={job_fake} /> */}
        { job && <Fulfillment job={job} made_for_you={user.age < 16 ? job.made_for_you_14_v2 : job.made_for_you_16_v2} /> }
        { job && <WorkEnvironment job={job} gender={gender} onClickChat={handleClickChat} loading={false}/> }
        {/* <HowToArrive job={job_fake} /> */}
        { job && <CareerEvolution job={job} onClickGritAndFit={handleClickGritAndFit}/> }

      </main>

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context="Tu as déjà rencontré 2 professionnels"
        reason={premiumModalReason}
      />
    </div>
  );
};
//   return (
//     <div className="flex flex-1 flex-col h-dvh w-full items-center py-6 px-4 no-scrollbar bg-teal-500">
//       {job &&
//         <div
//           className="flex-1 relative overflow-y-auto no-scrollbar
//           max-w-5xl w-full mx-auto d-vh mb-4 md:px-0
//           flex flex-col gap-6 max-h-screen bg-gray-500 items-center"
//         >
//           <div className="w-full flex flex-wrap gap-4">
//             <img className="w-40 h-auto object-cover" src={job.aws_pictures_m[1]} />
//             <div className="flex-1 gap-2">
//               <div className="items-center mb-2">
//                 {/* Emoji métier */}
//                 <h3 className="text-lg font-semibold text-white">{job.title_m}</h3>
//               </div>
//             </div>
//           </div>
//         </div>
//       }
//     </div>
//   );
// }